import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { Award, Loader2, X, CheckCircle2, XCircle, Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { fireConfettiCannons } from "@/lib/confetti";
import MasterySeal from "@/components/MasterySeal";

/**
 * BossQuizModal — Iter76 / P2 end-of-path mastery challenge.
 *
 * Flow
 * ----
 * intro → quiz → grading → result(passed | failed) → seal(passed)
 *
 * - Backend serves 10 LLM-generated questions stripped of answers.
 * - 80% pass threshold (8/10). Time pressure is mild: 5 min for the
 *   whole quiz, displayed as a thin progress bar so it nudges rather
 *   than punishes.
 * - On pass-and-first-time: +100 XP, +3 credits, mints a Mastery Seal
 *   that we render full-screen with confetti before closing.
 * - On re-take pass: still shows the existing Seal but skips reward
 *   grant + celebration.
 * - On fail: per-question breakdown with explanations + "Try again"
 *   button that restarts the attempt fresh.
 */
const TIME_LIMIT_SEC = 300; // 5 minutes — generous

export default function BossQuizModal({ open, onOpenChange, pathId, pathGoal }) {
  const [phase, setPhase] = useState("intro");
  const [attempt, setAttempt] = useState(null); // {attempt_id, questions, reward_*, already_passed_path}
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!open) {
      // Hard reset on close — don't leak state into the next attempt.
      setPhase("intro"); setAttempt(null); setAnswers({});
      setResult(null); setError(null); setStartedAt(null);
    }
  }, [open]);

  // Lightweight 1-Hz ticker for the timer pill.
  useEffect(() => {
    if (phase !== "quiz" || !startedAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [phase, startedAt]);

  const elapsedSec = useMemo(
    () => (startedAt ? Math.min(TIME_LIMIT_SEC, Math.floor((now - startedAt) / 1000)) : 0),
    [now, startedAt],
  );

  const startAttempt = async () => {
    setPhase("loading");
    setError(null);
    try {
      const { data } = await api.post("/boss-quiz/generate", { path_id: pathId });
      setAttempt(data);
      setAnswers({});
      setStartedAt(Date.now());
      setNow(Date.now());
      setPhase("quiz");
    } catch (e) {
      const msg = e?.response?.data?.detail
        || "Couldn't build a Boss Quiz from this path — try again in a moment.";
      setError(msg);
      setPhase("intro");
    }
  };

  const submit = async () => {
    if (!attempt || submitting) return;
    setSubmitting(true);
    try {
      const ordered = attempt.questions.map((_, i) => answers[i] ?? -1);
      const { data } = await api.post("/boss-quiz/submit", {
        attempt_id: attempt.attempt_id,
        answers: ordered,
      });
      setResult(data);
      if (data?.passed) {
        if (data.seal_minted) {
          fireConfettiCannons();
          setTimeout(fireConfettiCannons, 600);
        }
        if (data?.xp?.leveled_up) {
          window.dispatchEvent(new CustomEvent("oak:level-up", { detail: data.xp }));
        }
        window.dispatchEvent(new Event("oak:xp-updated"));
        if (data.granted_credits > 0) {
          window.dispatchEvent(new CustomEvent("oak:credits-updated", { detail: { credits: data.credits } }));
        }
        setPhase(data.seal_minted ? "seal" : "result");
      } else {
        setPhase("result");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit the quiz — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const tryAgain = () => {
    setResult(null);
    setAnswers({});
    setAttempt(null);
    setPhase("intro");
  };

  const total = attempt?.questions?.length || 10;
  const answeredCount = Object.keys(answers).length;
  const allAnswered = answeredCount === total;
  const timeLeftSec = Math.max(0, TIME_LIMIT_SEC - elapsedSec);
  const timeMin = Math.floor(timeLeftSec / 60);
  const timeRem = timeLeftSec % 60;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0B0E14]/95 border-white/10 text-white max-w-2xl p-0 overflow-hidden max-h-[90vh] flex flex-col"
        data-testid="boss-quiz-modal"
      >
        {/* Sticky header */}
        <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 grid place-items-center rounded-lg bg-gradient-to-br from-[#0044FF]/20 to-[#5D3FD3]/20 border border-[#5D3FD3]/40 shrink-0">
              <Award size={16} className="text-[#bcd1ff]" />
            </div>
            <div className="min-w-0">
              <div className="font-display text-sm tracking-tight">Boss Quiz · Final Mastery</div>
              <div className="text-[10.5px] text-white/45 font-mono-oak truncate max-w-[280px] sm:max-w-md">
                {pathGoal || "End-of-course exam"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {phase === "quiz" && (
              <div
                className={`text-[10.5px] font-mono-oak px-2 py-1 rounded-full border ${
                  timeLeftSec < 60
                    ? "border-rose-400/40 bg-rose-500/10 text-rose-200"
                    : "border-white/10 bg-white/[0.05] text-white/70"
                }`}
                data-testid="boss-quiz-timer"
              >
                {String(timeMin).padStart(2, "0")}:{String(timeRem).padStart(2, "0")}
              </div>
            )}
            <button
              onClick={() => onOpenChange?.(false)}
              className="h-7 w-7 grid place-items-center rounded-md text-white/45 hover:text-white hover:bg-white/5"
              aria-label="Close"
              data-testid="boss-quiz-close"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1">
          {phase === "loading" && (
            <div className="py-16 grid place-items-center text-white/55 text-sm" data-testid="boss-quiz-loading">
              <Loader2 size={18} className="animate-spin mb-3" />
              <div>Composing your 10-question exam…</div>
              <div className="text-[11px] text-white/35 font-mono-oak mt-1.5">Synthesising across every step</div>
            </div>
          )}

          {phase === "intro" && (
            <IntroPanel
              pathGoal={pathGoal}
              error={error}
              onStart={startAttempt}
              data-testid="boss-quiz-intro"
            />
          )}

          {phase === "quiz" && attempt && (
            <QuizBody
              questions={attempt.questions}
              answers={answers}
              setAnswer={(i, v) => setAnswers((a) => ({ ...a, [i]: v }))}
            />
          )}

          {phase === "result" && result && (
            <ResultBody
              result={result}
              alreadyPassed={!!result?.already_had_seal}
              onTryAgain={tryAgain}
              onClose={() => onOpenChange?.(false)}
            />
          )}

          {phase === "seal" && result && (
            <SealCelebration
              result={result}
              pathGoal={pathGoal}
              onClose={() => onOpenChange?.(false)}
            />
          )}
        </div>

        {phase === "quiz" && attempt && (
          <div className="px-5 py-3 border-t border-white/8 flex items-center justify-between gap-3 shrink-0">
            <div className="text-[11px] text-white/55 font-mono-oak">
              {answeredCount} / {total} answered
            </div>
            <button
              onClick={submit}
              disabled={!allAnswered || submitting}
              className="btn-primary px-5 py-2 rounded-full font-display font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              data-testid="boss-quiz-submit"
            >
              {submitting ? <><Loader2 size={13} className="animate-spin" /> Grading…</> : "Submit final answers"}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function IntroPanel({ pathGoal, error, onStart }) {
  return (
    <div className="px-6 py-8 sm:py-12 text-center max-w-md mx-auto" data-testid="boss-quiz-intro">
      <motion.div
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="mx-auto h-20 w-20 grid place-items-center rounded-2xl bg-gradient-to-br from-[#0044FF]/22 to-[#5D3FD3]/22 border border-white/12 mb-4"
      >
        <Award size={32} className="text-[#bcd1ff]" />
      </motion.div>
      <div className="label-eyebrow text-[#9bc7ff] mb-1.5">Final mastery</div>
      <h3 className="font-display text-2xl text-white tracking-tight">
        Take the Boss Quiz
      </h3>
      <p className="text-[13px] text-white/60 mt-2 leading-relaxed">
        10 questions synthesised across every step of <span className="text-white">{pathGoal || "this course"}</span>. Score 80%+ to unlock the animated Mastery Seal, plus <span className="text-[#9bffcc]">+100 XP</span> and <span className="text-[#9bffcc]">+3 credits</span>.
      </p>
      <ul className="text-[11.5px] text-white/45 font-mono-oak mt-4 space-y-1.5">
        <li>· 5 minute generous timer</li>
        <li>· 1 correct answer per question</li>
        <li>· Re-takes get fresh questions</li>
      </ul>
      {error && (
        <div className="mt-4 text-[12px] text-rose-200 bg-rose-500/8 border border-rose-400/25 rounded-lg px-3 py-2" data-testid="boss-quiz-error">
          {error}
        </div>
      )}
      <button
        onClick={onStart}
        className="mt-6 btn-primary px-6 py-2.5 rounded-full font-display font-semibold text-sm inline-flex items-center gap-2"
        data-testid="boss-quiz-start"
      >
        <Sparkles size={13} /> Start the quiz
      </button>
    </div>
  );
}

function QuizBody({ questions, answers, setAnswer }) {
  return (
    <div className="px-5 py-5 space-y-5" data-testid="boss-quiz-body">
      {questions.map((q, qi) => (
        <motion.div
          key={qi}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: qi * 0.04 }}
          className="rounded-xl border border-white/10 bg-white/[0.02] p-4"
          data-testid={`boss-quiz-q-${qi}`}
        >
          <div className="flex items-start gap-2.5 mb-3">
            <span className="text-[10.5px] font-mono-oak text-white/35 mt-1 shrink-0">Q{qi + 1}</span>
            <div className="text-[14px] text-white leading-snug font-display">{q.q}</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {q.options.map((opt, oi) => {
              const selected = answers[qi] === oi;
              return (
                <button
                  key={oi}
                  type="button"
                  onClick={() => setAnswer(qi, oi)}
                  className={`text-left text-[12.5px] px-3 py-2 rounded-lg border transition leading-snug ${
                    selected
                      ? "border-[#0044FF]/55 bg-[#0044FF]/12 text-white"
                      : "border-white/8 bg-white/[0.025] text-white/75 hover:bg-white/[0.05] hover:border-white/20"
                  }`}
                  data-testid={`boss-quiz-q-${qi}-opt-${oi}`}
                >
                  <span className="font-mono-oak text-[10px] text-white/40 mr-1.5">{String.fromCharCode(65 + oi)}.</span>
                  {opt}
                </button>
              );
            })}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function ResultBody({ result, alreadyPassed, onTryAgain, onClose }) {
  const passed = result.passed;
  return (
    <div className="px-5 py-6" data-testid="boss-quiz-result">
      <div className="text-center mb-5">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.35 }}
          className={`mx-auto h-16 w-16 grid place-items-center rounded-2xl border ${
            passed
              ? "bg-[#00FF9D]/15 border-[#00FF9D]/40"
              : "bg-rose-500/12 border-rose-400/35"
          }`}
        >
          {passed
            ? <CheckCircle2 size={26} className="text-[#9bffcc]" />
            : <XCircle size={26} className="text-rose-300" />}
        </motion.div>
        <div className="font-display text-2xl mt-3" data-testid="boss-quiz-result-headline">
          {passed
            ? (alreadyPassed ? "Pass again · seal already minted" : "Mastered.")
            : "Not quite — let's review."}
        </div>
        <div className="text-[12.5px] text-white/55 mt-1 font-mono-oak">
          Score <span className="text-white">{result.score}</span> / {result.total} · {Math.round((result.score / result.total) * 100)}% · Pass = 80%
        </div>
        {passed && !alreadyPassed && result.granted_credits > 0 && (
          <div className="text-[12px] text-[#9bffcc] mt-2">
            +{result.granted_credits} credit{result.granted_credits === 1 ? "" : "s"} · +{result.xp?.awarded || 0} XP awarded
          </div>
        )}
      </div>

      <div className="space-y-2 mb-5">
        {(result.per_question || []).map((q, qi) => (
          <div
            key={qi}
            className={`rounded-lg border p-3 ${
              q.is_right
                ? "border-[#00FF9D]/22 bg-[#00FF9D]/[0.04]"
                : "border-rose-400/22 bg-rose-500/[0.04]"
            }`}
            data-testid={`boss-quiz-review-${qi}`}
          >
            <div className="flex items-start gap-2 text-[12.5px] text-white/85 leading-snug">
              {q.is_right
                ? <CheckCircle2 size={12} className="text-[#9bffcc] mt-0.5 shrink-0" />
                : <XCircle size={12} className="text-rose-300 mt-0.5 shrink-0" />}
              <div className="min-w-0">
                <div className="font-display">{q.q}</div>
                {!q.is_right && (
                  <>
                    <div className="text-[11px] text-white/45 mt-1.5">
                      Your answer: <span className="line-through">{q.chosen >= 0 ? q.options[q.chosen] : "—"}</span>
                    </div>
                    <div className="text-[11px] text-[#9bffcc] mt-0.5">
                      Correct: {q.options[q.correct]}
                    </div>
                  </>
                )}
                {q.explanation && (
                  <div className="text-[10.5px] text-white/55 italic mt-1.5">{q.explanation}</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 justify-end">
        <button
          onClick={onTryAgain}
          className="text-[12px] btn-mint-outline px-4 py-2 rounded-full inline-flex items-center gap-1.5"
          data-testid="boss-quiz-retake"
        >
          <RefreshCw size={11} /> Take it again
        </button>
        <button
          onClick={onClose}
          className="text-[12px] btn-primary px-4 py-2 rounded-full font-display font-semibold"
          data-testid="boss-quiz-result-close"
        >
          Done
        </button>
      </div>
    </div>
  );
}

function SealCelebration({ result, pathGoal, onClose }) {
  return (
    <div className="px-5 py-8 text-center" data-testid="boss-quiz-seal">
      <MasterySeal
        pathGoal={pathGoal}
        score={result.score}
        total={result.total}
        mintedAt={new Date().toISOString()}
      />
      <div className="text-[12.5px] text-white/60 mt-5 max-w-sm mx-auto leading-relaxed">
        Mastery Seal minted. <span className="text-[#9bffcc]">+{result.granted_credits} credit{result.granted_credits === 1 ? "" : "s"}</span>, <span className="text-[#9bffcc]">+{result.xp?.awarded || 0} XP</span> added to your account. View it anytime in your library.
      </div>
      <button
        onClick={onClose}
        className="mt-5 btn-primary px-6 py-2.5 rounded-full font-display font-semibold text-sm inline-flex items-center gap-2"
        data-testid="boss-quiz-seal-close"
      >
        <Sparkles size={13} /> Continue learning
      </button>
    </div>
  );
}
